import React, { useState, useMemo, useRef, useEffect } from "react";
import { get, useController, useFormContext } from "react-hook-form";
import { useCombobox } from "downshift";
import { FormFieldType } from "storybook/utils/form";
import { InputLabelDefault } from "storybook/components/input/theme/default";
import {
  List,
  ListItem,
  ListContainer,
  StyledPopper,
} from "storybook/components/multi-select-autocomplete/theme/default";
import { FieldErrorDefault } from "storybook/components/field-error";
import { StyledInput, StyledInputContainer } from "./styles";

export interface TypeaheadInputType<T> extends FormFieldType<T> {
  label: string;
  items: string[];
  disabled?: boolean;
  placeholder?: string;
  hideErrorText?: boolean;
  hideDirty?: boolean;
  maxResults?: number;
}

export function TypeaheadInput<T>({
  name,
  label,
  items,
  disabled,
  hideDirty,
  placeholder,
  hideErrorText,
  maxResults = 8,
}: TypeaheadInputType<T>) {
  const { formState } = useFormContext();
  const { field } = useController({ name });
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [filterValue, setFilterValue] = useState(field.value || "");
  const isDisabled = disabled || formState.isSubmitting;
  const fieldError = get(formState.errors, name);
  const hasError = Boolean(fieldError);
  const isDirty = Boolean(get(formState.dirtyFields, name));

  // Compute filtered items based on filterValue (not controlled by downshift)
  const filteredItems = useMemo(() => {
    if (!filterValue?.trim()) return [];
    return items
      .filter((item) =>
        item.toLowerCase().includes(filterValue.trim().toLowerCase())
      )
      .slice(0, maxResults);
  }, [items, filterValue, maxResults]);

  const {
    isOpen,
    getItemProps,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    setInputValue,
  } = useCombobox({
    items: filteredItems,
    initialInputValue: field.value || "",
    onInputValueChange: ({ inputValue: newInputValue }) => {
      setFilterValue(newInputValue || "");
      field.onChange(newInputValue || "");
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        field.onChange(selectedItem);
      }
    },
  });

  // Sync downshift state when field value changes externally (e.g., form reset)
  useEffect(() => {
    if (field.value !== filterValue) {
      setFilterValue(field.value || "");
      setInputValue(field.value || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const showList = isOpen && filteredItems.length > 0;

  const ListComponent = useMemo(() => {
    return (
      <ListContainer
        {...getMenuProps({ disabled: isDisabled }, { suppressRefError: true })}
      >
        {showList && (
          <List>
            {filteredItems.map((item, index) => (
              <ListItem
                key={item}
                $highlighted={highlightedIndex === index}
                {...getItemProps({ item, index })}
              >
                {item}
              </ListItem>
            ))}
          </List>
        )}
      </ListContainer>
    );
  }, [
    showList,
    isDisabled,
    getItemProps,
    getMenuProps,
    filteredItems,
    highlightedIndex,
  ]);

  return (
    <StyledInputContainer data-testid="TypeaheadInput">
      <InputLabelDefault
        $size="medium"
        $hasError={hasError}
        $isDirty={isDirty}
        $hideLabel={false}
        $disabled={isDisabled}
        $variant="default"
        $alignText="right"
        $hideDirty={hideDirty || false}
        {...getLabelProps({ disabled: isDisabled })}
      >
        <span>{label}</span>
        <div ref={inputContainerRef} style={{ flex: 1 }}>
          <StyledInput
            $error={hasError}
            $disabled={isDisabled}
            $isDirty={isDirty}
            placeholder={placeholder}
            {...getInputProps(
              { disabled: isDisabled },
              { suppressRefError: true }
            )}
          />
        </div>
      </InputLabelDefault>
      <StyledPopper
        keepMounted={true}
        open={showList}
        anchorEl={inputContainerRef.current}
        placement="bottom-end"
        $hasItems={filteredItems.length > 0}
        modifiers={{
          flip: { enabled: true },
        }}
      >
        {ListComponent}
      </StyledPopper>
      {hasError && !hideErrorText && (
        <FieldErrorDefault>{fieldError?.message}</FieldErrorDefault>
      )}
    </StyledInputContainer>
  );
}
